import React, { useState, useEffect } from 'react';
import {
	Card,
	CardContent,
	Alert,
	AlertTitle,
	Spinner,
	PlatformMark
} from '../ui';
import { CheckCircledIcon, CrossCircledIcon } from '@radix-ui/react-icons';
import { useScanProgress } from '../../contexts/ScanProgressContext';

/**
 * ScanProgress - what a manager sees while a scan runs.
 *
 * Written in the product's language, not the pipeline's. The previous version
 * exposed the machinery: a "Job Creation Phase" box, "Jobs Created: 3 / 4",
 * window counts, per-phase percentage bars. None of that answers the only
 * questions a talent manager has while waiting: how far along is it, what has
 * it found, and is anything flagged. Three human steps now carry the whole
 * story - Collecting posts → AI risk analysis → Report ready - with live
 * counts, and the internals stay in the logs where they belong.
 */
export function ScanProgress({ scanId, onComplete, onError }) {
	const { progressData, error: progressError, retryScan } = useScanProgress(scanId);
	const [localError, setLocalError] = useState(null);

	useEffect(() => {
		if (progressData?.status === "completed" && onComplete) {
			onComplete(progressData);
		} else if (progressData?.status === "failed") {
			const errorMessage = progressData.error || "Scan failed";
			setLocalError(errorMessage);
			if (onError) {
				onError(errorMessage);
			}
		}
	}, [progressData, onComplete, onError]);

	const loading = progressData?.isLoading ?? true;
	const error = localError || progressError;

	if (loading) {
		return (
			<Card>
				<CardContent className="flex items-center justify-center p-8">
					<Spinner className="mr-3" />
					<span>Starting the scan…</span>
				</CardContent>
			</Card>
		);
	}

	if (error) {
		return (
			<Alert variant="destructive">
				<AlertTitle>Scan failed</AlertTitle>
				<p className="mt-2">{error}</p>
				{retryScan && (
					<button
						onClick={() => {
							setLocalError(null);
							retryScan();
						}}
						className="mt-3 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm"
					>
						Retry scan
					</button>
				)}
			</Alert>
		);
	}

	if (!progressData) return null;

	const { progress = {}, status, platform, dateRange } = progressData;
	const overall = Math.min(100, Math.round(progress.overallProgress || 0));
	const postsFound = progress.postsFound || 0;
	const postsAnalyzed = progress.postsAnalyzed || 0;
	const flaggedPosts = progress.flaggedPosts || 0;

	const isCompleted = status === 'completed';
	const isAnalyzing = status === 'analyzing';
	// Everything before analysis - initializing, queuing_jobs, pending,
	// processing - is "collecting" as far as the user cares.
	const isCollecting = !isCompleted && !isAnalyzing;

	const headline = isCompleted
		? 'Scan complete'
		: isAnalyzing
			? 'Analyzing content for risk'
			: 'Collecting posts';

	const formatDate = (date) => (date ? new Date(date).toLocaleDateString() : '');

	const steps = [
		{
			label: 'Collecting posts',
			state: isCollecting ? 'active' : 'done',
			detail: postsFound > 0
				? `${postsFound} post${postsFound === 1 ? '' : 's'} found`
				: isCollecting ? 'Searching the account…' : 'No posts in this period',
		},
		{
			label: 'AI risk analysis',
			state: isCompleted ? 'done' : isAnalyzing ? 'active' : 'pending',
			detail: postsAnalyzed > 0
				? `${postsAnalyzed} of ${Math.max(postsFound, postsAnalyzed)} analyzed`
				: isAnalyzing ? 'Reviewing each post…' : 'Starts when collection finishes',
			flag: flaggedPosts > 0 ? `${flaggedPosts} flagged` : null,
		},
		{
			label: 'Report ready',
			state: isCompleted ? 'done' : 'pending',
			detail: isCompleted ? 'Full results below' : 'Risk verdict and evidence',
		},
	];

	const StepGlyph = ({ state }) => {
		if (state === 'done') return <CheckCircledIcon className="h-5 w-5 text-green-600" />;
		if (state === 'active') return <Spinner size="sm" />;
		return <span className="block h-2 w-2 rounded-full bg-input mx-1.5" />;
	};

	return (
		<Card>
			<CardContent className="p-6 space-y-6">
				{/* Header */}
				<div className="flex items-center justify-between gap-3">
					<div className="flex items-center gap-3 min-w-0">
						<PlatformMark platform={platform} size="lg" />
						<div className="min-w-0">
							<h3 className="text-lg font-semibold text-foreground">{headline}</h3>
							<p className="text-sm text-muted-foreground truncate">
								{formatDate(dateRange?.startDate)} - {formatDate(dateRange?.endDate)}
							</p>
						</div>
					</div>
					{isCompleted ? (
						<CheckCircledIcon className="h-6 w-6 shrink-0 text-green-600" />
					) : status === 'failed' ? (
						<CrossCircledIcon className="h-6 w-6 shrink-0 text-red-600" />
					) : (
						<span className="shrink-0 text-sm font-semibold text-foreground">{overall}%</span>
					)}
				</div>

				{/* One bar for the whole scan */}
				<div className="w-full bg-accent rounded-full h-2">
					<div
						className="bg-brand-600 h-2 rounded-full transition-all duration-500"
						style={{ width: `${isCompleted ? 100 : overall}%` }}
					/>
				</div>

				{/* The three things that actually happen */}
				<ol className="space-y-4">
					{steps.map((step) => (
						<li key={step.label} className="flex items-start gap-3">
							<span className="mt-0.5 flex h-5 w-5 items-center justify-center shrink-0">
								<StepGlyph state={step.state} />
							</span>
							<div className="min-w-0 flex-1">
								<div className="flex items-center gap-2">
									<span className={`text-sm font-medium ${step.state === 'pending' ? 'text-muted-foreground' : 'text-foreground'}`}>
										{step.label}
									</span>
									{step.flag && (
										<span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
											{step.flag}
										</span>
									)}
								</div>
								<p className={`text-xs ${step.state === 'pending' ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
									{step.detail}
								</p>
							</div>
						</li>
					))}
				</ol>
			</CardContent>
		</Card>
	);
}

export default ScanProgress;
